import { motion } from "framer-motion";
import { Magnet, Feather, Layers, Zap } from "lucide-react";
import { IMAGES } from "@/lib/api";

const reveal = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08 } }),
};

export const Features = () => (
  <section id="features" data-testid="features-section" className="py-24 md:py-32 bg-stone-100/50 border-y border-stone-200">
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <div className="max-w-2xl mb-16">
        <p className="text-xs tracking-[0.25em] uppercase font-bold text-stone-500 mb-6">The solution</p>
        <h2 className="font-serif text-4xl md:text-6xl tracking-tight leading-tight text-stone-900">
          The stylus becomes <span className="italic">part of the case.</span>
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        <motion.div
          variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} custom={0}
          data-testid="feature-magnetic"
          className="md:col-span-8 rounded-3xl bg-stone-900 text-stone-50 p-10 md:p-14 overflow-hidden relative min-h-[340px] flex flex-col justify-between"
        >
          <div className="relative z-10 max-w-sm">
            <Magnet className="w-8 h-8 mb-6" strokeWidth={1.5} />
            <h3 className="text-2xl md:text-3xl font-bold mb-3">Magnetic integration</h3>
            <p className="text-stone-300 leading-relaxed">
              The ultra-thin stylus snaps flush into a precision-machined recessed channel — becoming one with the case.
            </p>
          </div>
          <img
            src={IMAGES.flatSnap}
            alt="Flat stylus magnetically snapping into the back channel"
            className="absolute right-0 bottom-0 w-3/5 h-full object-cover opacity-90 hidden md:block"
            style={{ maskImage: "linear-gradient(to right, transparent, black 35%)" }}
          />
        </motion.div>

        <motion.div
          variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} custom={1}
          data-testid="feature-bulk"
          className="md:col-span-4 rounded-3xl bg-white border border-stone-200 p-10 flex flex-col justify-between min-h-[340px]"
        >
          <Layers className="w-8 h-8 text-stone-800" strokeWidth={1.5} />
          <div>
            <h3 className="text-2xl font-bold text-stone-900 mb-3">Zero added bulk</h3>
            <p className="text-stone-600 leading-relaxed">
              Keeps the original ergonomics and slim profile you paid a premium for.
            </p>
          </div>
        </motion.div>

        <motion.div
          variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} custom={2}
          data-testid="feature-magsafe"
          className="md:col-span-4 rounded-3xl bg-white border border-stone-200 p-10 flex flex-col justify-between min-h-[300px]"
        >
          <Zap className="w-8 h-8 text-stone-800" strokeWidth={1.5} />
          <div>
            <h3 className="text-2xl font-bold text-stone-900 mb-3">MagSafe native</h3>
            <p className="text-stone-600 leading-relaxed">
              MagSafe is Apple's magnetic system on the back of the iPhone. Stylo keeps it fully intact — chargers, wallets and mounts still snap on and work perfectly.
            </p>
          </div>
        </motion.div>

        <motion.div
          variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} custom={3}
          data-testid="feature-precision"
          className="md:col-span-8 rounded-3xl bg-white border border-stone-200 p-10 md:p-14 flex items-center gap-8 min-h-[300px]"
        >
          <img src={IMAGES.heroBlack} alt="Stylo case detail" className="w-40 md:w-52 rounded-2xl object-cover hidden sm:block" />
          <div>
            <Feather className="w-8 h-8 text-stone-800 mb-5" strokeWidth={1.5} />
            <h3 className="text-2xl md:text-3xl font-bold text-stone-900 mb-3">Pro precision, always on hand</h3>
            <p className="text-stone-600 leading-relaxed max-w-md">
              An ultra-thin stylus engineered for professional accuracy — signing, sketching and editing wherever you are.
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);
